import { useState, type ReactNode } from 'react';
import { CircleNotchIcon, SealCheckIcon } from '@phosphor-icons/react';
import { msg, T, useGT, useMessages } from 'gt-react';
import { Button } from 'src/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from 'src/components/ui/alert-dialog';
import { useToast } from 'src/context/ToastContext';
import { useAgentDefinitionForm } from './AgentDefinitionFormContext';
import { AgentActionTooltip } from './AgentActionTooltip';
import {
  DiscardAgentDraftButton,
  EditAgentButton,
  SaveDraftButton,
} from './agentDefinitionEditorActions';
import { errorMessage, reactToFinalize } from './agentDefinitionEditActions';

const PUBLISH_TOOLTIP = msg(
  'Lock this draft and make new runs use it. You confirm before it is published.',
  { context: 'tooltip for publishing an embedded agent draft' },
);

export function AgentDefinitionSaveButton({
  label,
  className,
}: {
  label?: ReactNode;
  className?: string;
}) {
  const { isEditable } = useAgentDefinitionForm();
  if (!isEditable) return <EditAgentButton className={className} />;
  return (
    <>
      <SaveDraftButton label={label} className={className} />
      <EmbeddedPublishControls />
      <DiscardAgentDraftButton />
    </>
  );
}

function EmbeddedPublishControls() {
  const gt = useGT();
  const m = useMessages();
  const { showToast } = useToast();
  const { finalizeDraft, isSaving, isDraftActionPending } =
    useAgentDefinitionForm();
  const [publishOpen, setPublishOpen] = useState(false);
  const [staleMessage, setStaleMessage] = useState<string | null>(null);
  const busy = publishBusy(isSaving, isDraftActionPending);

  async function handlePublish() {
    try {
      const outcome = await finalizeDraft(
        true,
        acknowledgingStale(staleMessage),
      );
      reactToFinalize(outcome, {
        showStale: (warning) => setStaleMessage(warning.message),
        closeAndToast: () => {
          setPublishOpen(false);
          setStaleMessage(null);
          showToast({
            title: gt('Draft published', {
              context: 'toast after publishing an embedded agent draft',
            }),
            message: gt('New agent runs will use this version.', {
              context: 'toast after publishing an embedded agent draft',
            }),
            color: 'success',
          });
        },
      });
    } catch (publishError) {
      showToast({
        title: gt('Could not publish draft', {
          context: 'toast after embedded agent draft publish failure',
        }),
        message: errorMessage(publishError),
        color: 'error',
      });
    }
  }

  return (
    <>
      <AgentActionTooltip label={m(PUBLISH_TOOLTIP)}>
        <span className="inline-flex">
          <Button
            onClick={() => setPublishOpen(true)}
            disabled={busy}
            size="sm"
          >
            <PublishIcon pending={isDraftActionPending} />
            <T context="button, publish embedded agent definition draft">
              Publish
            </T>
          </Button>
        </span>
      </AgentActionTooltip>
      <EmbeddedPublishDialog
        open={publishOpen}
        staleMessage={staleMessage}
        busy={busy}
        onOpenChange={(open) => {
          setPublishOpen(open);
          clearStaleOnClose(open, () => setStaleMessage(null));
        }}
        onConfirm={() => void handlePublish()}
      />
    </>
  );
}

function publishBusy(saving: boolean, pending: boolean): boolean {
  if (saving) return true;
  return pending;
}

function acknowledgingStale(message: string | null): boolean {
  return message !== null;
}

function clearStaleOnClose(open: boolean, clear: () => void): void {
  if (open) return;
  clear();
}

function EmbeddedPublishDialog({
  open,
  staleMessage,
  busy,
  onOpenChange,
  onConfirm,
}: {
  open: boolean;
  staleMessage: string | null;
  busy: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
}) {
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            <StaleTitle stale={staleMessage !== null} />
          </AlertDialogTitle>
          <AlertDialogDescription>
            <StaleDescription message={staleMessage} />
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={busy}>
            <T context="button, cancel publishing embedded agent draft">
              Cancel
            </T>
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={(event) => {
              event.preventDefault();
              onConfirm();
            }}
            disabled={busy}
          >
            <BusySpinner pending={busy} />
            <PublishConfirmLabel stale={staleMessage !== null} />
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function StaleTitle({ stale }: { stale: boolean }) {
  if (stale) {
    return (
      <T context="dialog title, stale embedded agent draft">
        A newer version is published
      </T>
    );
  }
  return (
    <T context="dialog title, publish embedded agent draft">
      Publish this draft?
    </T>
  );
}

function StaleDescription({ message }: { message: string | null }) {
  if (message) return message;
  return (
    <T context="dialog description, publish embedded agent draft">
      The draft will be finalized and used for new agent runs.
    </T>
  );
}

function PublishConfirmLabel({ stale }: { stale: boolean }) {
  if (stale) {
    return (
      <T context="button, publish stale embedded agent draft">Publish anyway</T>
    );
  }
  return (
    <T context="button, confirm publishing embedded agent draft">Publish</T>
  );
}

function BusySpinner({ pending }: { pending: boolean }) {
  if (!pending) return null;
  return <CircleNotchIcon className="mr-2 h-4 w-4 animate-spin" />;
}

function PublishIcon({ pending }: { pending: boolean }) {
  if (pending) {
    return <CircleNotchIcon className="mr-2 h-4 w-4 animate-spin" />;
  }
  return <SealCheckIcon className="h-4 w-4 mr-2" />;
}
